import * as React from 'react';
import { Typography, Button } from 'components/common';
import { useNotification, useUser } from '@/hooks';
import { LayoutLogin } from '@/components/layout';
import AppRoutes from '@/const/routes';
import { useRouter } from 'next/router';
import CustomInputOtp from '@/components/common/custom-input-otp/CustomInputOtp';
import clsx from 'clsx';
import { CircleAlert } from 'lucide-react';
import { verificationsService } from '@/api';
import { useSession } from 'next-auth/react';
import { formatErrorMessage } from '@/lib';
import { ErrorHttpResponse, RoleType } from '@/interfaces';

interface TwoFactorAuthScreenProps {
	role: RoleType;
	type: 'verify-phone' | 'enter-code';
}

export const TwoFactorAuthScreen: React.FC<TwoFactorAuthScreenProps> = ({
	role,
	type,
}): JSX.Element => {
	const router = useRouter();
	const { user } = useUser();
	const { case_id, evaluated } = router.query;
	const { data: session, update } = useSession();
	const { toastSuccess, toastError } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [errorVerification, setErrorVerification] = React.useState<
		string | React.ReactNode
	>('');
	const [code, setCode] = React.useState<string>('');
	const [phone, setPhone] = React.useState<string>('');

	React.useEffect(() => {
		if (session && user) {
			const lastNumber = session.user.user.phonenumber as string;
			setPhone(lastNumber?.slice(-2) || user?.phonenumber?.slice(-2) || ''); // last two digits
		}
	}, [session, user]);

	const handleChangeCode = (code: string): void => {
		setCode(code);
		setErrorVerification('');
	};

	const handleVerifyCode = async (): Promise<void> => {
		setIsLoading(true);
		try {
			await verificationsService.validatePhoneOtp(
				code,
				type === 'verify-phone' ? 'phone_verification' : '2fa'
			);
			await update({
				...session,
				user: {
					...session?.user,
					is_partial_login: false,
				},
			});
			if (type === 'verify-phone')
				toastSuccess('Your phone number has been successfully verified.');
			if (session?.user.user.role === 'client') {
				const redirectRoute = evaluated
					? `${AppRoutes.CLIENTAPP_DASHBOARD}?evaluated=${evaluated}&case_id=${case_id}`
					: AppRoutes.CLIENTAPP_DASHBOARD;
				router.push(redirectRoute);
			} else {
				router.push(AppRoutes.LAWYERAPP_DASHBOARD);
			}
		} catch (error) {
			if ((error as ErrorHttpResponse).statusCode === 400) {
				setErrorVerification(
					<span>
						The verification code has expired. <br />
						Please request a new one and try again.
					</span>
				);
			} else {
				setErrorVerification(
					'The verification code is incorrect. Please try again.'
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleSendCodeAgain = async (): Promise<void> => {
		try {
			setCode('');
			setErrorVerification('');
			await verificationsService.verifyNumber(
				type === 'verify-phone' ? 'phone_verification' : '2fa'
			);
			toastSuccess('Verification code has been successfully sent.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	return (
		<LayoutLogin
			title={type === 'enter-code' ? 'Enter your verification code' : ''}
			customTitle={
				type === 'verify-phone' && role === 'lawyer' ? (
					<>
						Verify Your <span className="text-primary">Phone</span>
					</>
				) : (
					''
				)
			}
			rol={role}
			buttonBack
		>
			<div className="flex flex-col items-center justify-between w-full">
				{type === 'enter-code' ? (
					<Typography
						type="regular-18"
						className={'text-opacity-black text-center mb-10'}
					>
						{`We've sent a code to your phone number ending in XXXXXX${phone}.`}
					</Typography>
				) : (
					<Typography
						type="regular-18"
						className={'text-black text-center mb-12'}
					>
						{`We've sent a code to your phone number ending in`} <br />{' '}
						{`XXXXXX${phone}.`}
					</Typography>
				)}
				<CustomInputOtp
					length={4}
					value={code}
					onChange={handleChangeCode}
					classNameSlot={clsx('bg-white border-opacity-primary', {
						'ring-1 ring-main-red': errorVerification,
					})}
				/>
				{errorVerification && (
					<div className="flex gap-2 mt-2">
						<CircleAlert className="text-main-red !h-4 !w-4" />
						<Typography type="regular-12" className="text-main-red">
							{errorVerification}
						</Typography>
					</div>
				)}
				<Typography type="regular-16" className="mt-4 text-center">
					{`Didn't receive the code? `}
					<button
						type="button"
						className="text-primary font-semibold"
						onClick={handleSendCodeAgain}
					>
						Resend verification code
					</button>
				</Typography>
				<Button
					dataCY="signin"
					size="full"
					decoration="primary"
					label={type === 'enter-code' ? 'Sign In' : 'Verify'}
					loading={isLoading}
					disabled={isLoading || !code || code.length < 4}
					className={clsx('mt-8', {
						'!w-[247px] !h-[64px]': type === 'verify-phone',
					})}
					onClick={handleVerifyCode}
				/>
			</div>
		</LayoutLogin>
	);
};
